'use client';

import { createContext, useContext, type ReactNode } from 'react';

/**
 * Carries the route's rendered body through to wherever the composed shell tree
 * expects it.
 *
 * <p>The shell arrives as JSON walked by the tree renderer, and React children
 * cannot travel through JSON. So the mount layer publishes the body here and a
 * sentinel `PageContent` block inside the tree reads it back out. Placement of
 * the body is therefore owned by the operator-authored shell, not by this app —
 * which is the point.
 */
export const PageContentContext = createContext<ReactNode>(null);

export function PageContentProvider({
  children,
  pageContent,
}: {
  children: ReactNode;
  pageContent: ReactNode;
}) {
  return <PageContentContext.Provider value={pageContent}>{children}</PageContentContext.Provider>;
}

export function usePageContent(): ReactNode {
  return useContext(PageContentContext);
}

/** Registered as block type `PageContent` in the storefront block resolver. */
export function PageContentBlock() {
  return <>{usePageContent()}</>;
}
